import { useTranslation } from "react-i18next";
import { Button, Icon, cn } from "@nexus/ui";
import { useFavoriteReport } from "../../hooks/useFavoriteReport";
import type { PreflightReportSummary } from "../../types/preflight.types";

const FAVORITE_BUTTON =
  "shrink-0 rounded-md border transition-[color,background-color,border-color] duration-150 ease-out hover:[&_svg]:scale-125 [&_svg]:text-inherit";

/** The star grows on hover while the button keeps its footprint, so no neighbour shifts. */
const FAVORITE_ICON =
  "transition-transform duration-200 ease-out motion-reduce:transition-none";

const FAVORITE_BUTTON_ON =
  "border-warning-border bg-warning-subtle text-warning hover:bg-warning-subtle hover:text-warning";

const FAVORITE_BUTTON_OFF =
  "border-border bg-muted text-muted-foreground/60 hover:text-warning";

/**
 * `row` keeps the table cell's 28px footprint so no column has to widen;
 * `header` matches the 32px detail-page action buttons next to it.
 */
const BUTTON_SIZE = { row: "icon", header: "icon-lg" } as const;
const ICON_SIZE = { row: "xs", header: "sm" } as const;

export interface ReportFavoriteToggleProps {
  report: Pick<PreflightReportSummary, "id" | "name" | "isFavorite">;
  placement?: keyof typeof BUTTON_SIZE;
}

/**
 * Star toggle for one report. Starring stays visible without a hover — that is
 * the point of a favorite — so the control is always rendered, not revealed.
 */
export function ReportFavoriteToggle({
  report,
  placement = "row",
}: ReportFavoriteToggleProps) {
  const { t } = useTranslation();
  const favoriteMutation = useFavoriteReport(report);
  const isFavorite = report.isFavorite;
  const label = isFavorite
    ? t("preflight.favorite.remove", { name: report.name })
    : t("preflight.favorite.add", { name: report.name });

  return (
    <Button
      type="button"
      variant="ghost"
      size={BUTTON_SIZE[placement]}
      aria-label={label}
      aria-pressed={isFavorite}
      onClick={(event) => {
        // A table row opens the report on click; starring must not navigate.
        event.stopPropagation();
        favoriteMutation.mutate(!isFavorite);
      }}
      className={cn(
        FAVORITE_BUTTON,
        isFavorite ? FAVORITE_BUTTON_ON : FAVORITE_BUTTON_OFF,
      )}
    >
      <Icon
        name="star"
        size={ICON_SIZE[placement]}
        color="inherit"
        className={cn(FAVORITE_ICON, isFavorite && "fill-current")}
      />
    </Button>
  );
}
